import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ProfilesService } from '../../../services/profiles.service';
import { GroupsService } from '../../../services/groups.service';
import { PermissionsService } from '../../../services/permissions.service';
import { ProfilePermissionOverrideService } from '../../../services/profile-permission-override.service';
import { ProfileGroupAssociationsService } from '../../../services/profile-group-associations.service';
import { Group } from '../../../models/Group';
import { Permission } from '../../../models/Permission';
import { ProfilePermissionOverride } from '../../../models/ProfilePermissionOverride';
import { AuthService } from '../../../services/auth.service';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { forkJoin, of } from 'rxjs';
import { ConfigOrder, ConfigOrdersService } from '../../../services/config-orders.service';
import { ProfileConfigOrdersAssociationsService } from '../../../services/profile-configorders-associations.service';

@Component({
  selector: 'app-profile-create',
  templateUrl: './profile-create.component.html',
  styleUrls: ['./profile-create.component.css']
})
export class ProfileCreateComponent implements OnInit {
  profileForm!: FormGroup;
  groups: Group[] = [];
  permissions: Permission[] = [];
  groupPermissionsMap: Map<number, Permission[]> = new Map();
  configOrders: ConfigOrder[] = [];
  loading = false;
  loadingGroups = false;
  loadingPermissions = false;
  loadingConfigOrders = false;
  showGroupsSection = false;
  showOverridesSection = false;
  showConfigOrdersSection = false;
  currentUser: string = '';
  
  // Propriedades para modo de edição
  isEditMode = false;
  profileId: number | null = null;
  profileTitle = 'Criar Novo Perfil';
  originalGroupIds: number[] = []; // Grupos originalmente associados ao perfil
  originalOverrideIds: number[] = []; // IDs dos overrides originais
  
  // Mapa de permissões selecionadas: permissionId -> { overrideType, existingOverrideId? }
  permissionOverridesMap: Map<number, { overrideType: 'Allow' | 'Deny', existingOverrideId?: number }> = new Map();

  // Snapshot dos overrides originais (para salvar apenas diffs no modo de edição)
  originalPermissionOverridesMap: Map<number, { overrideType: 'Allow' | 'Deny', existingOverrideId: number }> = new Map();
  
  // Controle de colapso dos grupos de permissões
  collapsedGroups: Set<number> = new Set();

  // Lazy-load / colapso das seções
  overridesExpanded = false;
  private permissionsLoadedForOverrides = false;

  configOrdersExpanded = false;
  private configOrdersLoaded = false;

  // Snapshot dos ConfigOrders associados ao perfil (para salvar diffs no modo de edição)
  originalConfigOrdersIds: number[] = [];
  
  // Pesquisa de permissões
  permissionSearchTerm: string = '';
  
  // Propriedades do modal de sucesso
  showSuccessModal = false;
  successModalTitle = 'Sucesso!';
  successModalMessage = '';

  constructor(
    private fb: FormBuilder,
    private profilesService: ProfilesService,
    private groupsService: GroupsService,
    private permissionsService: PermissionsService,
    private overrideService: ProfilePermissionOverrideService,
    private profileGroupAssociationsService: ProfileGroupAssociationsService,
    private configOrdersService: ConfigOrdersService,
    private profileConfigOrdersAssociationsService: ProfileConfigOrdersAssociationsService,
    private authService: AuthService,
    private confirmationService: ConfirmationService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.getCurrentUser();
    
    // Carregar grupos primeiro, depois verificar modo de edição
    this.loadGroups().then(() => {
      this.checkEditMode();
    });
  }

  /**
   * Verifica se está em modo de edição e carrega dados do perfil
   */
  checkEditMode(): void {
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.isEditMode = true;
        this.profileId = +params['id'];
        this.profileTitle = 'Editar Perfil';
        this.loadProfileData(this.profileId);
      }
    });
  }

  /**
   * Carrega dados do perfil para edição
   * Assumindo que os grupos já foram carregados no ngOnInit
   */
  loadProfileData(profileId: number): void {
    this.loading = true;
    
    
// Carregar dados em paralelo
    forkJoin({
      profile: this.profilesService.getProfileById(profileId),
      overrides: this.overrideService.getAllOverridesByProfile(profileId),
      associations: this.profileGroupAssociationsService.getProfileGroupAssociationsByProfile(profileId, 1, 1000)
    }).subscribe({
      next: ({ profile, overrides, associations }) => {
        
        // Preencher informações básicas do perfil
        this.profileForm.patchValue({
          name: profile.name,
          description: profile.description || ''
        });
        
        // Processar associações de grupos se existirem
        if (associations && associations.data && associations.data.length > 0) {
          this.processGroupsForEdit(associations.data);
        }
        
        // Processar overrides se existirem
        if (overrides && Array.isArray(overrides) && overrides.length > 0) {
          this.processOverridesForEdit(overrides);
        }
        
        this.loading = false;
      },
      error: (error) => {
        console.error('❌ Erro ao carregar perfil:', error);
        this.showErrorMessage('Erro ao carregar dados do perfil.');
        this.router.navigate(['/admin/profiles']);
        this.loading = false;
      }
    });
  }

  /**
   * Processa associações de grupos para modo de edição
   */
  processGroupsForEdit(associations: any[]): void {

    // Ativar seção de grupos
    this.profileForm.patchValue({ includeGroups: true });
    this.showGroupsSection = true;

    // Extrair IDs de grupo suportando diferentes formatos do backend (GroupId / groupId)
    const extractedGroupIds: number[] = associations
      .map((association: any) => {
        const id = association.groupId ?? association.GroupId ?? association.GroupID ?? association.groupID;
        return id !== undefined && id !== null ? Number(id) : null;
      })
      .filter((id: any) => id !== null) as number[];

    // Armazenar grupos originais para posterior comparação
    this.originalGroupIds = extractedGroupIds;

    // Limpar seleção atual e popular com os grupos extraídos (evita estados antigos)
    this.clearSelectedGroups();

    extractedGroupIds.forEach(groupId => {
      this.selectedGroups.push(this.fb.control(groupId));

      // Tentar obter o nome do grupo para logs (procurar no objeto ou na lista de grupos carregada)
      const assoc = associations.find((a: any) => {
        const id = a.groupId ?? a.GroupId ?? a.groupID ?? a.GroupID;
        return Number(id) === groupId;
      }) || {};

      const groupName = assoc.groupName ?? assoc.GroupName ?? this.groups.find(g => g.id === groupId)?.name ?? '';
    });

  }

  /**
   * trackBy para otimizar renderização da lista de grupos
   */
  trackByGroupId(index: number, group: Group): number {
    return group.id;
  }

  /**
   * Processa overrides para modo de edição
   */
  processOverridesForEdit(overrides: ProfilePermissionOverride[]): void {
    // Ativar seção de overrides
    this.profileForm.patchValue({ includeOverrides: true });
    this.showOverridesSection = true;
    
    // Armazenar IDs dos overrides originais
    this.originalOverrideIds = overrides.map(override => override.id);

    // Limpar snapshots/estado para evitar resíduos entre trocas de rota
    this.permissionOverridesMap.clear();
    this.originalPermissionOverridesMap.clear();
    
    // Popular o mapa de permissões selecionadas
    overrides.forEach(override => {
      const overrideType = override.overrideType === 'Grant' ? 'Allow' : (override.overrideType as 'Allow' | 'Deny');
      this.permissionOverridesMap.set(override.permissionId, {
        overrideType,
        existingOverrideId: override.id
      });

      // Snapshot original para comparação no salvar
      this.originalPermissionOverridesMap.set(override.permissionId, {
        overrideType,
        existingOverrideId: override.id
      });
    });

    // Mantém a seção minimizada; permissões serão carregadas ao clicar
    this.overridesExpanded = false;
  }

  /**
   * Carrega todas as permissões para modo de edição
   */
  async loadAllPermissionsForEdit(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.loadingPermissions = true;
      this.permissionsService.getAllPermissions().subscribe({
        next: (permissions) => {
          this.permissions = permissions;
          this.loadingPermissions = false;
          resolve();
        },
        error: (error) => {
          console.error('❌ Erro ao carregar permissões:', error);
          this.loadingPermissions = false;
          reject(error);
        }
      });
    });
  }

  /**
   * Inicializa o formulário reativo
   */
  initializeForm(): void {
    this.profileForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(100)]],
      description: ['', [Validators.maxLength(500)]],
      includeGroups: [false],
      includeOverrides: [false],
      includeConfigOrders: [false],
      selectedGroups: this.fb.array([]),
      overrides: this.fb.array([]),
      selectedConfigOrders: this.fb.array([])
    });

    // Observa mudanças no checkbox de grupos existentes
    this.profileForm.get('includeGroups')?.valueChanges.subscribe(value => {
      this.showGroupsSection = value;
      if (!value) {
        this.clearSelectedGroups();
      }
    });

    // Observa mudanças no checkbox de overrides
    this.profileForm.get('includeOverrides')?.valueChanges.subscribe(value => {
      this.showOverridesSection = value;
      if (!value) {
        this.clearOverrides();
        this.overridesExpanded = false;
        this.permissionsLoadedForOverrides = false;
        this.groupPermissionsMap.clear();
        this.permissions = [];
        this.permissionSearchTerm = '';
      } else {
        // Lazy-load: só carrega permissões ao expandir a seção
        this.overridesExpanded = false;
      }
    });

    // Observa mudanças no checkbox de ConfigOrders
    this.profileForm.get('includeConfigOrders')?.valueChanges.subscribe(value => {
      this.showConfigOrdersSection = value;
      if (!value) {
        this.clearSelectedConfigOrders();
        this.configOrdersExpanded = false;
        this.configOrdersLoaded = false;
        this.configOrders = [];
        this.originalConfigOrdersIds = [];
      } else {
        // Lazy-load: carrega apenas ao expandir
        this.configOrdersExpanded = false;
      }
    });
  }

  /**
   * Obtém o usuário atual do localStorage
   */
  getCurrentUser(): void {
    const lastLoggedEmail = localStorage.getItem('lastLoggedEmail');
    
    if (lastLoggedEmail) {
      this.currentUser = lastLoggedEmail;
      return;
    }

    const currentUserJson = localStorage.getItem('currentUser');
    if (currentUserJson) {
      try {
        const user = JSON.parse(currentUserJson);
        this.currentUser = user.email || '';
        
      } catch (error) {
        console.error('Erro ao obter usuário:', error);
        this.currentUser = '';
      }
    } else {
      this.currentUser = '';
    }
  }

  /**
   * Carrega os grupos disponíveis
   */
  loadGroups(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.loadingGroups = true;
      this.profilesService.getAllGroups().subscribe({
        next: (groups) => {
          this.groups = groups;
          this.loadingGroups = false;
          resolve();
        },
        error: (error) => {
          console.error('Erro ao carregar grupos:', error);
          this.loadingGroups = false;
          this.showWarningMessage('Não foi possível carregar os grupos. O perfil será criado sem grupos associados.');
          resolve(); // Resolve mesmo com erro para não bloquear o fluxo
        }
      });
    });
  }

  /**
   * Getter para o FormArray de grupos selecionados
   */
  get selectedGroups(): FormArray {
    return this.profileForm.get('selectedGroups') as FormArray;
  }

  /**
   * Getter para o FormArray de overrides
   */
  get overrides(): FormArray {
    return this.profileForm.get('overrides') as FormArray;
  }

  /**
   * Getter para o FormArray de ConfigOrders selecionadas
   */
  get selectedConfigOrders(): FormArray {
    return this.profileForm.get('selectedConfigOrders') as FormArray;
  }

  /**
   * Adiciona um checkbox de grupo selecionado
   */
  onGroupCheckboxChange(event: any, groupId: number): void {
    if (event.target.checked) {
      this.selectedGroups.push(this.fb.control(groupId));
    } else {
      const index = this.selectedGroups.controls.findIndex(x => x.value === groupId);
      if (index >= 0) {
        this.selectedGroups.removeAt(index);
      }
    }
  }

  /**
   * Verifica se um grupo está selecionado
   */
  isGroupSelected(groupId: number): boolean {
    return this.selectedGroups.controls.some(x => x.value === groupId);
  }

  /**
   * Verifica se um grupo estava originalmente associado ao perfil
   */
  isGroupOriginallyAssociated(groupId: number): boolean {
    return this.originalGroupIds.includes(groupId);
  }

  /**
   * Limpa todos os grupos selecionados
   */
  clearSelectedGroups(): void {
    while (this.selectedGroups.length !== 0) {
      this.selectedGroups.removeAt(0);
    }
  }

  /**
   * Limpa todos os overrides
   */
  clearOverrides(): void {
    while (this.overrides.length !== 0) {
      this.overrides.removeAt(0);
    }
    this.permissionOverridesMap.clear();
  }

  clearSelectedConfigOrders(): void {
    while (this.selectedConfigOrders.length !== 0) {
      this.selectedConfigOrders.removeAt(0);
    }
  }

  onConfigOrderCheckboxChange(event: any, configOrderId: number): void {
    if (event.target.checked) {
      this.selectedConfigOrders.push(this.fb.control(configOrderId));
    } else {
      const index = this.selectedConfigOrders.controls.findIndex(x => x.value === configOrderId);
      if (index >= 0) {
        this.selectedConfigOrders.removeAt(index);
      }
    }
    this.profileForm.markAsDirty();
  }

  isConfigOrderSelected(configOrderId: number): boolean {
    return this.selectedConfigOrders.controls.some(x => x.value === configOrderId);
  }

  isConfigOrderOriginallyAssociated(configOrderId: number): boolean {
    return this.originalConfigOrdersIds.includes(configOrderId);
  }

  trackByConfigOrderId(index: number, item: ConfigOrder): number {
    return item.id;
  }

  toggleConfigOrdersExpanded(): void {
    this.configOrdersExpanded = !this.configOrdersExpanded;
    if (this.configOrdersExpanded) {
      this.ensureConfigOrdersLoaded();
    }
  }

  private ensureConfigOrdersLoaded(): void {
    if (this.configOrdersLoaded) return;

    this.loadingConfigOrders = true;

    const configOrders$ = this.configOrdersService.getAll();
    const associatedIds$ = (this.isEditMode && this.profileId)
      ? this.profileConfigOrdersAssociationsService.getConfigOrdersIdsByProfile(this.profileId)
      : of<number[]>([]);

    forkJoin({
      configOrders: configOrders$,
      associatedIds: associatedIds$
    }).subscribe({
      next: ({ configOrders, associatedIds }) => {
        this.configOrders = Array.isArray(configOrders) ? configOrders : [];
        this.originalConfigOrdersIds = Array.isArray(associatedIds) ? associatedIds : [];

        // Preseleciona as associações existentes no modo de edição
        if (this.isEditMode && this.profileId) {
          this.clearSelectedConfigOrders();
          this.originalConfigOrdersIds.forEach(id => this.selectedConfigOrders.push(this.fb.control(id)));
        }

        this.configOrdersLoaded = true;
        this.loadingConfigOrders = false;
      },
      error: (error) => {
        console.error('❌ Erro ao carregar ConfigOrders:', error);
        this.loadingConfigOrders = false;
        this.configOrdersLoaded = false;
        this.showWarningMessage('Não foi possível carregar as Ordens de Serviço (ConfigOrders).');
      }
    });
  }

  toggleOverridesExpanded(): void {
    this.overridesExpanded = !this.overridesExpanded;
    if (this.overridesExpanded) {
      this.ensurePermissionsLoadedForOverrides();
    }
  }

  private ensurePermissionsLoadedForOverrides(): void {
    if (this.permissionsLoadedForOverrides) return;
    this.loadPermissionsForOverrides();
  }

  /**
   * Carrega permiss\u00f5es para configura\u00e7\u00e3o de overrides
   */
  loadPermissionsForOverrides(): void {
    this.loadingPermissions = true;
    
    this.permissionsService.getPermissions(1, 1000).subscribe({
      next: (response) => {
        if (response && response.data) {
          this.permissions = response.data.map(dto => ({
            id: dto.Id,
            name: dto.Name,
            description: dto.Description,
            groupId: dto.GroupId,
            createdAt: new Date(dto.CreatedAt),
            updatedAt: new Date(dto.UpdatedAt),
            createdBy: dto.CreatedBy,
            updatedBy: dto.UpdatedBy
          }));
        } else {
          this.permissions = [];
        }
        
        // Agrupar permissões por grupo
        this.groupPermissionsMap.clear();
        this.permissions.forEach(perm => {
          if (!this.groupPermissionsMap.has(perm.groupId)) {
            this.groupPermissionsMap.set(perm.groupId, []);
          }
          this.groupPermissionsMap.get(perm.groupId)!.push(perm);
        });
        this.permissionsLoadedForOverrides = true;
        this.loadingPermissions = false;
      },
      error: (error) => {
        console.error('Erro ao carregar permissões:', error);
        this.loadingPermissions = false;
        this.permissionsLoadedForOverrides = false;
        this.showWarningMessage('Não foi possível carregar as permissões.');
      }
    });
  }

  /**
   * Adiciona um override de permiss\u00e3o
   */
  addOverride(): void {
    const overrideForm = this.fb.group({
      permissionId: [null, [Validators.required]],
      overrideType: ['Grant', [Validators.required]],
      isActive: [true]
    });
    
    this.overrides.push(overrideForm);
  }

  /**
   * Remove um override
   */
  async removeOverride(index: number): Promise<void> {
    const overrideControl = this.overrides.at(index);
    const overrideId = overrideControl.get('id')?.value;
    
    // Se o override já existe no backend (tem ID), deletá-lo
    if (overrideId && this.isEditMode) {
      const confirmDelete = await this.confirmAction(
        'Deseja realmente remover este override? Esta ação não pode ser desfeita.',
        'Confirmar Remoção',
        'Remover',
        'danger',
        'btn-danger'
      );
      
      if (!confirmDelete) {
        return;
      }
      
      try {
        await this.overrideService.deleteOverride(overrideId, this.currentUser).toPromise();
        
        // Remover do array de IDs originais
        const originalIndex = this.originalOverrideIds.indexOf(overrideId);
        if (originalIndex > -1) {
          this.originalOverrideIds.splice(originalIndex, 1);
        }
      } catch (error) {
        console.error('❌ Erro ao deletar override:', error);
        this.showErrorMessage('Erro ao deletar override. Por favor, tente novamente.');
        return;
      }
    }
    
    // Remover do formulário
    this.overrides.removeAt(index);
  }

  /**
   * Obt\u00e9m o nome da permiss\u00e3o base
   */
  getBasePermissionName(permissionId: number): string {
    const permission = this.permissions.find(p => p.id === permissionId);
    return permission ? permission.name : '';
  }

  /**
   * Obt\u00e9m a descri\u00e7\u00e3o da permiss\u00e3o base
   */
  getBasePermissionDescription(permissionId: number): string {
    const permission = this.permissions.find(p => p.id === permissionId);
    return permission ? permission.description || '' : '';
  }

  /**
   * Obt\u00e9m o grupo de uma permiss\u00e3o
   */
  getPermissionGroup(permissionId: number): Group | undefined {
    const permission = this.permissions.find(p => p.id === permissionId);
    if (permission) {
      return this.groups.find(g => g.id === permission.groupId);
    }
    return undefined;
  }

  // ========================
  // Métodos para UI agrupada de permissões
  // ========================

  getPermissionGroupIds(): number[] {
    if (!this.permissionSearchTerm.trim()) {
      return Array.from(this.groupPermissionsMap.keys());
    }
    return Array.from(this.groupPermissionsMap.keys()).filter(groupId =>
      this.getPermissionsByGroupId(groupId).length > 0
    );
  }

  getPermissionsByGroupId(groupId: number): Permission[] {
    const perms = this.groupPermissionsMap.get(groupId) || [];
    if (!this.permissionSearchTerm.trim()) {
      return perms;
    }
    const term = this.permissionSearchTerm.trim().toLowerCase();
    return perms.filter(p =>
      p.name.toLowerCase().includes(term) ||
      (p.description && p.description.toLowerCase().includes(term))
    );
  }

  getAllPermissionsByGroupId(groupId: number): Permission[] {
    return this.groupPermissionsMap.get(groupId) || [];
  }

  getGroupName(groupId: number): string {
    const group = this.groups.find(g => g.id === groupId);
    return group ? group.name : `Grupo ${groupId}`;
  }

  toggleGroupCollapse(groupId: number): void {
    if (this.collapsedGroups.has(groupId)) {
      this.collapsedGroups.delete(groupId);
    } else {
      this.collapsedGroups.add(groupId);
    }
  }

  isGroupCollapsed(groupId: number): boolean {
    return this.collapsedGroups.has(groupId);
  }

  getPermissionOverrideType(permissionId: number): 'Allow' | 'Deny' | null {
    const entry = this.permissionOverridesMap.get(permissionId);
    return entry ? entry.overrideType : null;
  }

  setPermissionOverride(permissionId: number, overrideType: 'Allow' | 'Deny'): void {
    const existing = this.permissionOverridesMap.get(permissionId);
    this.permissionOverridesMap.set(permissionId, {
      overrideType,
      existingOverrideId: existing?.existingOverrideId
    });
    this.profileForm.markAsDirty();
  }

  removePermissionOverride(permissionId: number): void {
    this.permissionOverridesMap.delete(permissionId);
    this.profileForm.markAsDirty();
  }

  countAllowedInGroup(groupId: number): number {
    const perms = this.getAllPermissionsByGroupId(groupId);
    return perms.filter(p => this.getPermissionOverrideType(p.id) === 'Allow').length;
  }

  countDeniedInGroup(groupId: number): number {
    const perms = this.getAllPermissionsByGroupId(groupId);
    return perms.filter(p => this.getPermissionOverrideType(p.id) === 'Deny').length;
  }

  allowAllInGroup(groupId: number): void {
    const perms = this.getAllPermissionsByGroupId(groupId);
    perms.forEach(p => this.setPermissionOverride(p.id, 'Allow'));
  }

  denyAllInGroup(groupId: number): void {
    const perms = this.getAllPermissionsByGroupId(groupId);
    perms.forEach(p => this.setPermissionOverride(p.id, 'Deny'));
  }

  clearGroupPermissions(groupId: number): void {
    const perms = this.getAllPermissionsByGroupId(groupId);
    perms.forEach(p => this.permissionOverridesMap.delete(p.id));
    this.profileForm.markAsDirty();
  }

  allowAllPermissions(): void {
    this.permissions.forEach(p => this.setPermissionOverride(p.id, 'Allow'));
  }

  denyAllPermissions(): void {
    this.permissions.forEach(p => this.setPermissionOverride(p.id, 'Deny'));
  }

  clearAllPermissions(): void {
    this.permissionOverridesMap.clear();
    this.profileForm.markAsDirty();
  }

  /**
   * Verifica se um override tem erro
   */
  hasOverrideError(overrideIndex: number, fieldName: string, errorType: string): boolean {
    const field = this.overrides.at(overrideIndex).get(fieldName);
    return !!(field && field.hasError(errorType) && (field.dirty || field.touched));
  }

  /**
   * Obt\u00e9m mensagem de erro para campo de override
   */
  getOverrideErrorMessage(overrideIndex: number, fieldName: string): string {
    const field = this.overrides.at(overrideIndex).get(fieldName);
    
    if (field?.hasError('required')) {
      return 'Este campo \u00e9 obrigat\u00f3rio.';
    }
    
    if (field?.hasError('minlength')) {
      const minLength = field.errors?.['minlength'].requiredLength;
      return `M\u00ednimo de ${minLength} caracteres.`;
    }
    
    if (field?.hasError('maxlength')) {
      const maxLength = field.errors?.['maxlength'].requiredLength;
      return `M\u00e1ximo de ${maxLength} caracteres.`;
    }
    
    return '';
  }

  /**
   * Verifica se um campo tem erro
   */
  hasError(fieldName: string, errorType: string): boolean {
    const field = this.profileForm.get(fieldName);
    return !!(field && field.hasError(errorType) && (field.dirty || field.touched));
  }

  /**
   * Salva o perfil (criar ou atualizar)
   */
  async onSubmit(): Promise<void> {
    // Marca todos os campos como touched para exibir erros
    this.profileForm.markAllAsTouched();
    
    if (this.profileForm.invalid) {
      this.showErrorMessage('Formulário inválido: Por favor, corrija os erros antes de continuar.');
      return;
    }

    // Valida se o email do usuário está disponível
    if (!this.currentUser || this.currentUser.trim() === '') {
      this.showErrorMessage('Email do usuário não encontrado. Por favor, faça login novamente.');
      return;
    }

    this.loading = true;

    const formValue = this.profileForm.value;
    
    try {
      if (this.isEditMode && this.profileId) {
        // MODO DE EDIÇÃO
        await this.updateProfile(this.profileId, formValue);
      } else {
        // MODO DE CRIAÇÃO
        await this.createProfile(formValue);
      }
      
      // Exibir modal de sucesso
      this.successModalMessage = `Perfil ${this.isEditMode ? 'atualizado' : 'criado'} com sucesso!`;
      this.showSuccessModal = true;
    } catch (error: any) {
      console.error(`Erro ao ${this.isEditMode ? 'atualizar' : 'criar'} perfil:`, error);
      
      let errorMessage = `Ocorreu um erro ao ${this.isEditMode ? 'atualizar' : 'criar'} o perfil.`;
      
      if (error.error?.message) {
        errorMessage = error.error.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      this.showErrorMessage(`Erro ao ${this.isEditMode ? 'atualizar' : 'criar'} perfil: ` + errorMessage);
    } finally {
      this.loading = false;
    }
  }

  /**
   * Cria um novo perfil
   */
  async createProfile(formValue: any): Promise<void> {
    let createdProfile: any;
    
    // Perfil com grupos existentes
    if (formValue.includeGroups && formValue.selectedGroups.length > 0) {
      const profileData = {
        name: formValue.name.trim(),
        description: formValue.description?.trim() || '',
        groupIds: formValue.selectedGroups,
        createdBy: this.currentUser
      };

      createdProfile = await this.profilesService.createProfileWithGroups(profileData).toPromise();
    }
    // Perfil simples
    else {
      const profileData = {
        name: formValue.name.trim(),
        description: formValue.description?.trim() || '',
        createdBy: this.currentUser
      };

      createdProfile = await this.profilesService.createProfile(profileData).toPromise();
    }

    // Se houver overrides configurados no mapa, cria-os após criar o perfil
    if (formValue.includeOverrides && this.permissionOverridesMap.size > 0 && createdProfile?.id) {
      const overridePromises: Promise<any>[] = [];
      
      this.permissionOverridesMap.forEach((value, permissionId) => {
        const overrideData = {
          profileId: createdProfile.id,
          permissionId: permissionId,
          overrideType: value.overrideType as 'Allow' | 'Deny' | 'Custom',
          isActive: true,
          createdBy: this.currentUser
        };
        overridePromises.push(this.overrideService.createOverride(overrideData).toPromise());
      });

      await Promise.all(overridePromises);
    }

    // Se houver ConfigOrders selecionadas, associa após criar o perfil
    if (formValue.includeConfigOrders && Array.isArray(formValue.selectedConfigOrders) && formValue.selectedConfigOrders.length > 0 && createdProfile?.id) {
      await this.profileConfigOrdersAssociationsService.associateConfigOrdersToProfile({
        profileId: createdProfile.id,
        configOrdersIds: formValue.selectedConfigOrders,
        updatedBy: this.currentUser
      }).toPromise();
    }
  }

  /**
   * Atualiza um perfil existente
   */
  async updateProfile(profileId: number, formValue: any): Promise<void> {
    // Atualizar informações básicas do perfil
    const updateData = {
      name: formValue.name.trim(),
      description: formValue.description?.trim() || '',
      updatedBy: this.currentUser
    };

    await this.profilesService.updateProfile(profileId, updateData).toPromise();

    // Gerenciar associações e desassociações de grupos
    const currentGroupIds = formValue.includeGroups ? (formValue.selectedGroups as number[]) : [];
    
    // Identificar grupos a serem associados (novos)
    const groupsToAssociate = currentGroupIds.filter(id => !this.originalGroupIds.includes(id));
    
    // Identificar grupos a serem desassociados (removidos)
    const groupsToDisassociate = this.originalGroupIds.filter(id => !currentGroupIds.includes(id));
    
    // Desassociar grupos removidos
    if (groupsToDisassociate.length > 0) {
      
      try {
        await this.profileGroupAssociationsService.removeGroupsFromProfile({
          profileId: profileId,
          groupIds: groupsToDisassociate,
          updatedBy: this.currentUser
        }).toPromise();
      } catch (error) {
        console.error('❌ Erro ao desassociar grupos:', error);
      }
    }
    
    // Associar novos grupos
    if (groupsToAssociate.length > 0) {
      await this.profilesService.associateGroupsToProfile(profileId, groupsToAssociate, this.currentUser).toPromise();
    }

    // Gerenciar overrides (salvar apenas diffs)
    if (formValue.includeOverrides) {
      if (this.permissionOverridesMap.size > 0) {
        // IDs dos overrides originais que devem permanecer
        const keepExistingOverrideIds = new Set<number>();
        const toUpdate: { id: number; data: any }[] = [];
        const toCreate: any[] = [];

        for (const [permissionId, value] of this.permissionOverridesMap) {
          if (value.existingOverrideId) {
            keepExistingOverrideIds.add(value.existingOverrideId);
            const original = this.originalPermissionOverridesMap.get(permissionId);
            const hasChanged = !original || original.overrideType !== value.overrideType;
            if (hasChanged) {
              toUpdate.push({
                id: value.existingOverrideId,
                data: { overrideType: value.overrideType as 'Allow' | 'Deny' | 'Custom', isActive: true, updatedBy: this.currentUser }
              });
            }
          } else {
            toCreate.push({
              profileId: profileId,
              permissionId: permissionId,
              overrideType: value.overrideType as 'Allow' | 'Deny' | 'Custom',
              isActive: true,
              createdBy: this.currentUser
            });
          }
        }

        // 1º DELETAR — deve vir antes de criar para evitar conflito de duplicata
        const overridesToDelete = this.originalOverrideIds.filter(id => !keepExistingOverrideIds.has(id));
        for (const overrideId of overridesToDelete) {
          try {
            await this.overrideService.deleteOverride(overrideId, this.currentUser).toPromise();
          } catch (error) {
            console.error(`❌ Erro ao deletar override ${overrideId}:`, error);
          }
        }

        // 2º ATUALIZAR existentes
        for (const item of toUpdate) {
          await this.overrideService.updateOverride(item.id, item.data).toPromise();
        }

        // 3º CRIAR novos (após deletar, sem risco de "já existe")
        for (const createData of toCreate) {
          await this.overrideService.createOverride(createData).toPromise();
        }
      } else {
        // Seção ativa mas tudo foi limpo — deletar todos os overrides originais
        for (const overrideId of this.originalOverrideIds) {
          try {
            await this.overrideService.deleteOverride(overrideId, this.currentUser).toPromise();
          } catch (error) {
            console.error(`❌ Erro ao deletar override ${overrideId}:`, error);
          }
        }
      }
    } else {
      // Seção desativada — remover overrides existentes no backend (se houver)
      if (this.originalOverrideIds.length > 0) {
        for (const overrideId of this.originalOverrideIds) {
          try {
            await this.overrideService.deleteOverride(overrideId, this.currentUser).toPromise();
          } catch (error) {
            console.error(`❌ Erro ao deletar override ${overrideId}:`, error);
          }
        }
      }
    }

    // Gerenciar associações de ConfigOrders (apenas se a seção estiver ativa E a aba foi carregada)
    // Obs.: evita comportamento destrutivo quando o usuário só marca o switch e salva sem abrir a aba.
    if (formValue.includeConfigOrders && this.configOrdersLoaded) {

      const currentConfigOrdersIds = Array.isArray(formValue.selectedConfigOrders)
        ? (formValue.selectedConfigOrders as number[])
        : [];

      const configOrdersToAssociate = currentConfigOrdersIds.filter(id => !this.originalConfigOrdersIds.includes(id));
      const configOrdersToRemove = this.originalConfigOrdersIds.filter(id => !currentConfigOrdersIds.includes(id));

      if (configOrdersToRemove.length > 0) {
        await this.profileConfigOrdersAssociationsService.removeConfigOrdersFromProfile({
          profileId,
          configOrdersIds: configOrdersToRemove,
          updatedBy: this.currentUser
        }).toPromise();
      }

      if (configOrdersToAssociate.length > 0) {
        await this.profileConfigOrdersAssociationsService.associateConfigOrdersToProfile({
          profileId,
          configOrdersIds: configOrdersToAssociate,
          updatedBy: this.currentUser
        }).toPromise();
      }
    }
  }

  /**
   * Cancela e volta para a listagem
   */
  onCancel(): void {
    if (this.profileForm.dirty) {
      this.confirmationService.confirm({
        title: 'Confirmar Cancelamento',
        message: 'Existem alterações não salvas. Deseja realmente cancelar?',
        confirmText: 'Sim, Cancelar',
        cancelText: 'Continuar Editando',
        confirmButtonClass: 'btn-danger',
        cancelButtonClass: 'btn-secondary',
        type: 'warning'
      }).subscribe(confirmed => {
        if (confirmed) {
          this.router.navigate(['/admin/profiles']);
        }
      });
    } else {
      this.router.navigate(['/admin/profiles']);
    }
  }

  /**
   * Fecha o modal de sucesso e navega para a listagem
   */
  closeSuccessModal(): void {
    this.showSuccessModal = false;
    this.router.navigate(['/admin/profiles']);
  }

  /**
   * Obtém a mensagem de erro para um campo
   */
  getErrorMessage(fieldName: string): string {
    const field = this.profileForm.get(fieldName);
    
    if (field?.hasError('required')) {
      return 'Este campo é obrigatório.';
    }
    
    if (field?.hasError('minlength')) {
      const minLength = field.errors?.['minlength'].requiredLength;
      return `Mínimo de ${minLength} caracteres.`;
    }
    
    if (field?.hasError('maxlength')) {
      const maxLength = field.errors?.['maxlength'].requiredLength;
      return `Máximo de ${maxLength} caracteres.`;
    }
    
    return '';
  }

  private showInfoMessage(message: string, title: string = 'Informação'): void {
    this.confirmationService.confirm({
      title,
      message,
      type: 'info',
      confirmText: 'OK',
      confirmButtonClass: 'btn-primary',
      cancelButtonClass: 'd-none'
    }).subscribe();
  }

  private showWarningMessage(message: string, title: string = 'Aviso'): void {
    this.confirmationService.confirm({
      title,
      message,
      type: 'warning',
      confirmText: 'OK',
      confirmButtonClass: 'btn-primary',
      cancelButtonClass: 'd-none'
    }).subscribe();
  }

  private showErrorMessage(message: string, title: string = 'Erro'): void {
    this.confirmationService.confirm({
      title,
      message,
      type: 'danger',
      confirmText: 'OK',
      confirmButtonClass: 'btn-danger',
      cancelButtonClass: 'd-none'
    }).subscribe();
  }

  private async confirmAction(
    message: string,
    title: string = 'Confirmação',
    confirmText: string = 'Confirmar',
    type: 'warning' | 'danger' | 'info' | 'question' = 'question',
    confirmButtonClass: string = 'btn-primary'
  ): Promise<boolean> {
    return new Promise((resolve) => {
      this.confirmationService.confirm({
        title,
        message,
        type,
        confirmText,
        cancelText: 'Cancelar',
        confirmButtonClass,
        cancelButtonClass: 'btn-secondary'
      }).subscribe(confirmed => resolve(confirmed));
    });
  }
}
